import { FC, useEffect, useRef, useState } from "react";

import { Stack } from "@mui/material";
import type { AiSessionFeedbackReason } from "@simplyk/common";

import { AssistantFeedback } from "../AssistantFeedback/AssistantFeedback";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";

export interface AssistantFeedbackState {
  rating: number | null;
  comment: string | null;
  reason: AiSessionFeedbackReason | null;
  isSaving: boolean;
  hasFailed: boolean;
  rate: (rating: number, comment?: string) => Promise<void>;
  submitComment: (comment: string) => Promise<void> | undefined;
  selectReason?: (reason: AiSessionFeedbackReason) => Promise<void> | undefined;
}

interface ExitFeedbackProps {
  question: string;
  feedback: AssistantFeedbackState;
  onDone: () => void;
  maxRatingAskingComment?: number;
}

export const ExitFeedback: FC<ExitFeedbackProps> = ({ question, feedback, onDone, maxRatingAskingComment }) => {
  const { t } = useTranslate();
  const [draft, setDraft] = useState("");
  const hasRated = Boolean(feedback.rating);
  const latest = useRef({ draft, feedback });
  useEffect(() => {
    latest.current = { draft, feedback };
  });
  useEffect(
    () => () => {
      const { draft: unsent, feedback: state } = latest.current;
      if (state.rating && !state.comment && unsent.trim()) {
        void state.submitComment(unsent.trim());
      }
    },
    []
  );
  const rate = (rating: number) => {
    if (maxRatingAskingComment === undefined || rating <= maxRatingAskingComment) {
      void feedback.rate(rating);
      return;
    }
    const comment = draft.trim();
    latest.current = { draft: "", feedback };
    void feedback.rate(rating, comment || undefined);
    onDone();
  };
  const submitComment = (comment: string) => {
    latest.current = { draft: "", feedback };
    setDraft("");
    void feedback.submitComment(comment);
  };
  const { selectReason } = feedback;
  return (
    <Stack
      spacing={2}
      alignItems="center"
      data-test="assistant-exit-feedback"
      sx={(theme) => ({
        position: "absolute",
        inset: 0,
        zIndex: 1,
        p: 2,
        overflowY: "auto",
        bgcolor: theme.palette.surface.neutral.supershy,
      })}
    >
      <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ flex: 1, width: "100%" }}>
        <AssistantFeedback
          question={question}
          rating={feedback.rating}
          comment={feedback.comment}
          reason={feedback.reason}
          isSaving={feedback.isSaving}
          hasFailed={feedback.hasFailed}
          onRate={rate}
          onSubmitComment={submitComment}
          onSelectReason={selectReason && ((reason) => void selectReason(reason))}
          onDone={onDone}
          draft={draft}
          onDraftChange={setDraft}
        />
        <DashboardButton variant="text" disabled={feedback.isSaving} onClick={onDone}>
          {t("dashboard", hasRated ? "aiElements.feedback.close" : "aiElements.feedback.skip")}
        </DashboardButton>
      </Stack>
    </Stack>
  );
};
